import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { CogIcon } from '@navikt/aksel-icons';
import { OrgOverviewTopBar } from '../../../components/OrgOverviewTopBar';
import { ContainerCard } from '../../../components/ContainerCard/ContainerCard';
import { Spinner } from '../../../components/Spinner/Spinner';
import { Alert } from '../../../components/Alert/Alert';
import { Select } from '../../../components/Select/Select';
import { Label } from '../../../components/Label/Label';
import { Button } from '../../../components/Button';
import { useGetOrganizationQuery } from '../../../hooks/queries/useGetOrganizationQuery';
import { useUpdateOrganizationMutation } from '../../../hooks/mutations/useUpdateOrganizationMutation';
import classes from './OrganizationGeneralPage.module.css';

export const OrganizationGeneralPage = () => {
  const { t } = useTranslation();

  const { data: organization, isLoading, error } = useGetOrganizationQuery();

  const { mutate: updateOrganization } = useUpdateOrganizationMutation();

  const [workLanguage, setWorkLanguage] = useState<'NB' | 'EN' | 'inherit'>(
    organization?.workLanguage === null ||
      organization?.workLanguage === undefined
      ? 'inherit'
      : organization.workLanguage,
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Update local state when organization data loads
  useEffect(() => {
    if (organization?.workLanguage && workLanguage === 'inherit') {
      setWorkLanguage(organization.workLanguage);
    }
  }, [organization?.workLanguage]);

  const handleSave = () => {
    setErrorMessage(null);

    const payload = {
      workLanguage: workLanguage === 'inherit' ? null : workLanguage,
    };

    updateOrganization(payload, {
      onSuccess: () => {
        toast.success(t('organizationGeneral.toast.saveSuccess'), {
          position: 'bottom-right',
        });
      },
      onError: (error: any) => {
        const errorMsg =
          error?.response?.data?.message ||
          t('organizationGeneral.errors.saveError');
        setErrorMessage(errorMsg);
      },
    });
  };

  if (isLoading) {
    return (
      <>
        <OrgOverviewTopBar />
        <div className={classes.container}>
          <div className={classes.loadingContainer}>
            <Spinner label={t('organizationGeneral.loading')} />
          </div>
        </div>
      </>
    );
  }

  if (error || !organization) {
    return (
      <>
        <OrgOverviewTopBar />
        <div className={classes.container}>
          <ContainerCard>
            <ContainerCard.Body>
              <Alert
                variant="danger"
                size="md"
                title={t('organizationGeneral.errorTitle')}
              >
                {t('organizationGeneral.errorMessage')}
              </Alert>
            </ContainerCard.Body>
          </ContainerCard>
        </div>
      </>
    );
  }

  return (
    <>
      <OrgOverviewTopBar />
      <div className={classes.container}>
        <ContainerCard>
          <ContainerCard.Title icon={<CogIcon aria-hidden />}>
            {t('organizationGeneral.title')}
          </ContainerCard.Title>
          <ContainerCard.SubHeader>
            {t('organizationGeneral.description')}
          </ContainerCard.SubHeader>
          <ContainerCard.Body>
            <div className={classes.form}>
              <div className={classes.formBody}>
                <div>
                  <Label htmlFor="workLanguage" required={false}>
                    {t('organizationGeneral.fields.workLanguage.label')}
                  </Label>
                  <Select
                    id="workLanguage"
                    name="workLanguage"
                    value={workLanguage}
                    onChange={(value) => {
                      setErrorMessage(null);
                      setWorkLanguage(value);
                    }}
                    options={[
                      {
                        value: 'inherit',
                        label: t(
                          'organizationGeneral.fields.workLanguage.options.systemDefault',
                        ),
                      },
                      {
                        value: 'NB',
                        label: t(
                          'organizationGeneral.fields.workLanguage.options.norwegian',
                        ),
                      },
                      {
                        value: 'EN',
                        label: t(
                          'organizationGeneral.fields.workLanguage.options.english',
                        ),
                      },
                    ]}
                  />
                  <p className={classes.helpText}>
                    {t('organizationGeneral.fields.workLanguage.helpText')}
                  </p>
                </div>
              </div>

              {errorMessage && (
                <Alert
                  title={t('organizationGeneral.errors.saveErrorTitle')}
                  variant="danger"
                  size="sm"
                  className={classes.alert}
                >
                  {errorMessage}
                </Alert>
              )}

              <div className={classes.buttons}>
                <Button variant="primary" size="medium" onClick={handleSave}>
                  {t('organizationGeneral.actions.save')}
                </Button>
              </div>
            </div>
          </ContainerCard.Body>
        </ContainerCard>
      </div>
    </>
  );
};
